/**
 * PriorPeriodsEditor — Lets the user dial-in the YTD totals on a paystub
 * by editing every pay period BEFORE the earliest stub being generated.
 *
 * Example: if the user is generating bi-weekly stubs #12, #13, #14 of 2026,
 * this component renders editable rows for periods #1 through #11 with
 * defaults matching the current pay-period amounts.  Hit "Calculate YTD"
 * and we sum all prior-period rows together with the current period(s) of
 * each generated stub and write the result to that stub's `ytd` block.
 *
 * Each prior-period row exposes 6 columns: gross, federal tax, state tax,
 * Social Security, Medicare, SDI.  The user can override any value (e.g.
 * "I got a $5,000 bonus in March, $2,000 base every other period").
 *
 * Data shape stored in formData.priorPeriods:
 *   [
 *     { period: 1, gross: 2000, federal: 102.52, state: 60,
 *       socialSecurity: 124, medicare: 29, sdi: 18 },
 *     ...
 *   ]
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  ChevronDown,
  ChevronUp,
  History,
  Calculator,
  RefreshCw,
  Eraser,
} from "lucide-react";
import { toast } from "sonner";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Month label for a period number given the pay frequency. Useful for
// orienting the user when they see "Period 7" — we tell them roughly which
// month that lands in.
const monthLabelForPeriod = (periodNum, payFrequency, year) => {
  const periodsPerYear = {
    weekly: 52, "bi-weekly": 26, "semi-monthly": 24, monthly: 12,
  }[payFrequency] || 26;
  const daysPerPeriod = 365 / periodsPerYear;
  const startDayOfYear = Math.floor((periodNum - 1) * daysPerPeriod);
  const d = new Date(year, 0, 1 + startDayOfYear);
  return d.toLocaleString("en-US", { month: "short", day: "numeric" });
};

export default function PriorPeriodsEditor({ formData, updateFormData }) {
  const [open, setOpen] = useState(false);

  // Number of prior periods to render = (earliest generated stub #) - 1
  const { earliestStubNum, currentPerPeriod, priorCount, payFreq } = useMemo(() => {
    const stubs = formData.paystubs || [];
    let earliest = stubs.length
      ? Math.min(...stubs.map((s) => s.stubNumberInYear || s.stubNumber || 1))
      : (formData.stubNumberInYear || 1);
    // Match earliest current-period numbers from that stub so prior periods
    // default to "more of the same".  Falls back to the first stub if no
    // earliest match (shouldn't happen but defensive).
    const earliestStub = stubs.find(
      (s) => (s.stubNumberInYear || s.stubNumber) === earliest
    ) || stubs[0];
    const c = earliestStub?.current || {};
    return {
      earliestStubNum: earliest,
      payFreq: formData.payFrequency || "bi-weekly",
      priorCount: Math.max(0, earliest - 1),
      currentPerPeriod: {
        gross: round2(c.grossPay),
        federal: round2(c.federalTax),
        state: round2(c.stateTax),
        socialSecurity: round2(c.socialSecurity),
        medicare: round2(c.medicare),
        sdi: round2(c.sdi),
      },
    };
  }, [formData.paystubs, formData.payFrequency, formData.stubNumberInYear]);

  // Initialize the prior-periods array whenever the count changes (e.g.
  // user picks a different "first pay period" date or changes paystub
  // count).  We don't blow away user-edited values when only the count is
  // unchanged.
  useEffect(() => {
    const existing = formData.priorPeriods || [];
    if (existing.length === priorCount) return;
    const next = Array.from({ length: priorCount }, (_, i) => {
      // Reuse the user's existing edits where the index lines up.
      if (existing[i]) return existing[i];
      return { period: i + 1, ...currentPerPeriod };
    });
    updateFormData({ priorPeriods: next });
  }, [priorCount, earliestStubNum]);

  const priors = formData.priorPeriods || [];
  const year = formData.selectedYear || new Date().getFullYear();

  const updateRow = (idx, field, value) => {
    const next = priors.map((r, i) =>
      i === idx ? { ...r, [field]: toNum(value) } : r
    );
    updateFormData({ priorPeriods: next });
  };

  const fillAllWithCurrent = () => {
    const next = Array.from({ length: priorCount }, (_, i) => ({
      period: i + 1,
      ...currentPerPeriod,
    }));
    updateFormData({ priorPeriods: next });
    toast.success(`Reset ${priorCount} prior period${priorCount === 1 ? "" : "s"} to match current`);
  };

  // 2026-06-23 — one-tap helpers per user request.  Zero a single row or
  // every row across all 6 categories without the user having to clear
  // 6 fields by hand.
  const zeroRow = (idx) => {
    const blank = { period: idx + 1, gross: 0, federal: 0, state: 0, socialSecurity: 0, medicare: 0, sdi: 0 };
    updateFormData({ priorPeriods: priors.map((r, i) => (i === idx ? blank : r)) });
  };

  const zeroAll = () => {
    const next = Array.from({ length: priorCount }, (_, i) => ({
      period: i + 1, gross: 0, federal: 0, state: 0, socialSecurity: 0, medicare: 0, sdi: 0,
    }));
    // 2026-06-23 — auto-apply YTD calc right after clearing so the user
    // gets the "no prior pay → YTD = sum of new stubs only" result in
    // one tap instead of two.  Use functional update + state-derived
    // sums so we don't rely on stale closure data.
    updateFormData((prev) => {
      // priors = all zeros, so running starts at zero.
      const stubs = (prev.paystubs || []).slice();
      const ordered = stubs
        .map((s, idx) => ({ s, idx }))
        .sort((a, b) =>
          (a.s.stubNumberInYear || a.s.stubNumber || 0) -
          (b.s.stubNumberInYear || b.s.stubNumber || 0)
        );
      let running = { gross: 0, federal: 0, state: 0, socialSecurity: 0, medicare: 0, sdi: 0 };
      const newStubs = stubs.slice();
      for (const { s, idx } of ordered) {
        const c = s.current || {};
        running = {
          gross: running.gross + toNum(c.grossPay),
          federal: running.federal + toNum(c.federalTax),
          state: running.state + toNum(c.stateTax),
          socialSecurity: running.socialSecurity + toNum(c.socialSecurity),
          medicare: running.medicare + toNum(c.medicare),
          sdi: running.sdi + toNum(c.sdi),
        };
        const totalTaxes =
          running.federal + running.state + running.socialSecurity +
          running.medicare + running.sdi;
        newStubs[idx] = {
          ...s,
          ytd: {
            regular: round2(running.gross), overtime: 0, bonus: 0, commission: 0,
            tips: 0, holidayPay: 0, sickPay: 0, vacationPay: 0,
            grossPay: round2(running.gross),
            federalTax: round2(running.federal),
            stateTax: round2(running.state),
            socialSecurity: round2(running.socialSecurity),
            medicare: round2(running.medicare),
            sdi: round2(running.sdi),
            totalTaxes: round2(totalTaxes),
            deductions: 0,
            totalDeductions: round2(totalTaxes),
            netPay: round2(running.gross - totalTaxes),
          },
        };
      }
      return { priorPeriods: next, paystubs: newStubs };
    });
    toast.success(`Cleared all ${priorCount} prior periods · YTD now starts fresh from the stub you're making`);
  };

  const applyYTD = () => {
    // Sum every prior period across all 6 categories.
    const priorTotals = priors.reduce(
      (acc, row) => ({
        gross: acc.gross + toNum(row.gross),
        federal: acc.federal + toNum(row.federal),
        state: acc.state + toNum(row.state),
        socialSecurity: acc.socialSecurity + toNum(row.socialSecurity),
        medicare: acc.medicare + toNum(row.medicare),
        sdi: acc.sdi + toNum(row.sdi),
      }),
      { gross: 0, federal: 0, state: 0, socialSecurity: 0, medicare: 0, sdi: 0 }
    );

    // For each generated stub, the YTD = priorTotals + sum of its own
    // current + every OTHER generated stub whose period <= this stub.
    // Stubs are ordered with the most recent first, so iterate from the
    // earliest to the latest, accumulating.
    updateFormData((prev) => {
      const stubs = (prev.paystubs || []).slice();
      // Sort by stubNumberInYear ascending so YTD builds chronologically.
      const ordered = stubs
        .map((s, idx) => ({ s, idx }))
        .sort((a, b) =>
          (a.s.stubNumberInYear || a.s.stubNumber || 0) -
          (b.s.stubNumberInYear || b.s.stubNumber || 0)
        );

      // Start each running total at priorTotals; add each stub's current
      // period as we walk forward in time.
      let running = { ...priorTotals };
      const newStubs = stubs.slice(); // mutable copy keyed by original idx

      for (const { s, idx } of ordered) {
        const c = s.current || {};
        running = {
          gross: running.gross + toNum(c.grossPay),
          federal: running.federal + toNum(c.federalTax),
          state: running.state + toNum(c.stateTax),
          socialSecurity: running.socialSecurity + toNum(c.socialSecurity),
          medicare: running.medicare + toNum(c.medicare),
          sdi: running.sdi + toNum(c.sdi),
        };
        // Derive total taxes + net for the YTD snapshot.
        const totalTaxes =
          running.federal + running.state + running.socialSecurity +
          running.medicare + running.sdi;
        const ytd = {
          regular: round2(running.gross),
          overtime: 0,
          bonus: 0,
          commission: 0,
          tips: 0,
          holidayPay: 0,
          sickPay: 0,
          vacationPay: 0,
          grossPay: round2(running.gross),
          federalTax: round2(running.federal),
          stateTax: round2(running.state),
          socialSecurity: round2(running.socialSecurity),
          medicare: round2(running.medicare),
          sdi: round2(running.sdi),
          totalTaxes: round2(totalTaxes),
          deductions: 0,
          totalDeductions: round2(totalTaxes),
          netPay: round2(running.gross - totalTaxes),
        };
        newStubs[idx] = { ...s, ytd };
      }

      return { paystubs: newStubs };
    });

    toast.success(
      `YTD recalculated from ${priorCount} prior period${priorCount === 1 ? "" : "s"} + ${
        (formData.paystubs || []).length
      } current stub${(formData.paystubs || []).length === 1 ? "" : "s"}`
    );
  };

  if (priorCount === 0) {
    return null; // Nothing to edit (e.g. user is generating period #1)
  }

  return (
    <Card className="bg-zinc-900 border-zinc-700" data-testid="prior-periods-editor">
      <CardHeader
        className="cursor-pointer hover:bg-zinc-800/60 rounded-t-lg transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <CardTitle className="text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 rounded-lg bg-cyan-500/15 border border-cyan-500/40 flex-shrink-0">
              <History className="w-4 h-4 text-cyan-300" />
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base">Year-to-Date</span>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 font-semibold">
                  ✓ Auto-calculated
                </span>
                <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5">
                  Optional
                </span>
              </div>
              <span className="text-xs text-zinc-400 font-normal mt-1.5">
                {open
                  ? `Editing ${priorCount} prior ${payFreq} period${priorCount === 1 ? "" : "s"} (${monthLabelForPeriod(1, payFreq, year)} → ${monthLabelForPeriod(earliestStubNum - 1, payFreq, year)}). Numbers default to match the current paystub.`
                  : `YTD is already filled in for you. Tap here only if you want to edit the prior ${priorCount} ${payFreq} pay period${priorCount === 1 ? "" : "s"} by hand.`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!open && (
              <span className="hidden sm:inline text-xs text-cyan-300 font-semibold">Tap to edit</span>
            )}
            {open ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
          </div>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="pt-2 space-y-3">
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
            <p className="text-sm text-zinc-200 leading-relaxed">
              <span className="text-cyan-300 font-semibold">How this works:</span> each row below is one pay period from earlier this year. The numbers are pre-filled to match the current paystub. Change any number, then tap <span className="text-cyan-300 font-semibold">Calculate YTD</span> to update the &ldquo;Year-to-Date&rdquo; totals on every generated paystub.
            </p>
            <p className="text-xs text-zinc-500 mt-2">
              💡 If you don&apos;t need to edit anything, just close this section — YTD is already done for you.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={zeroAll}
              className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
              data-testid="prior-periods-zero-all"
              title="Set every prior period to $0 — useful for a brand-new hire"
            >
              <Eraser className="w-3 h-3 mr-1.5" /> Zero all rows
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={fillAllWithCurrent}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
              data-testid="prior-periods-reset"
            >
              <RefreshCw className="w-3 h-3 mr-1.5" /> Reset rows
            </Button>
            <Button
              size="sm"
              onClick={applyYTD}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="prior-periods-apply"
            >
              <Calculator className="w-3 h-3 mr-1.5" /> Calculate YTD
            </Button>
          </div>

          {/* Table — compact grid keyed by 6-column gross/fed/state/ss/med/sdi + Zero button */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-x-auto">
            <div
              className="grid text-[10px] uppercase tracking-wider text-zinc-500 px-2 py-2"
              style={{ gridTemplateColumns: "100px repeat(6, minmax(0, 1fr)) 50px" }}
            >
              <span>Period</span>
              <span className="text-right">Gross</span>
              <span className="text-right">Federal</span>
              <span className="text-right">State</span>
              <span className="text-right">SS</span>
              <span className="text-right">Medicare</span>
              <span className="text-right">SDI</span>
              <span className="text-center">Zero</span>
            </div>
            {priors.map((row, idx) => (
              <div
                key={idx}
                className="grid items-center gap-1 px-2 py-1.5 border-t border-zinc-800/60 hover:bg-zinc-900/40"
                style={{ gridTemplateColumns: "100px repeat(6, minmax(0, 1fr)) 50px" }}
                data-testid={`prior-period-row-${idx + 1}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-cyan-300">#{idx + 1}</span>
                  <span className="text-[10px] text-zinc-500">
                    {monthLabelForPeriod(idx + 1, payFreq, year)}
                  </span>
                </div>
                {["gross", "federal", "state", "socialSecurity", "medicare", "sdi"].map((field) => (
                  <Input
                    key={field}
                    type="number"
                    step="0.01"
                    value={row[field] ?? 0}
                    onChange={(e) => updateRow(idx, field, e.target.value)}
                    className="h-7 bg-zinc-900 border-zinc-700 text-white text-right text-xs px-2"
                    data-testid={`prior-period-${idx + 1}-${field}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => zeroRow(idx)}
                  className="h-7 w-9 mx-auto flex items-center justify-center rounded bg-zinc-900 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition-colors"
                  title={`Zero out every field on period #${idx + 1}`}
                  data-testid={`prior-period-${idx + 1}-zero`}
                >
                  <Eraser className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

        </CardContent>
      )}
    </Card>
  );
}
